import { useState } from "react";
import { useTheme } from "next-themes";
import { Moon, Sun, LogOut, Settings, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { SettingsDialog } from "@/components/SettingsDialog";
import { CompanyLogo } from "@/components/BrandMark";
import { DepotSwitcher } from "@/components/DepotSwitcher";
import { openInstallPopup } from "@/lib/installStore";
import { OwnerBanner } from "@/components/OwnerBanner";
import { TrialBanner } from "@/components/billing/TrialBanner";

export const Shell = ({ children, title, tabs }) => {
  const { theme, setTheme } = useTheme();
  const { user, logout } = useAuth();
  const [settingsOpen, setSettingsOpen] = useState(false);

  const initials = (user?.name || user?.email || "?").slice(0, 2).toUpperCase();
  const installed = typeof window !== "undefined" &&
    (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true);

  return (
    <div className="min-h-screen bg-background">
      <OwnerBanner />
      <TrialBanner />
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/85 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <span data-testid="app-logo" className="flex min-w-0 overflow-hidden"><CompanyLogo compact={user?.role === "coordinator"} /></span>
            <span className="text-[11px] text-muted-foreground hidden lg:inline">{title}</span>
            <DepotSwitcher />
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            {!installed && (
              <Button data-testid="header-install-btn" onClick={openInstallPopup} size="sm" className="rounded-full bg-primary hover:bg-primary/90 text-white h-9">
                <Download className="w-4 h-4 sm:mr-1.5" /> <span className="hidden sm:inline">Install</span>
              </Button>
            )}
            <Badge variant="outline" className="hidden lg:inline-flex capitalize border-primary/40 text-primary">
              {user?.role}
            </Badge>
            <div className="hidden lg:flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-xs font-bold font-mono">{initials}</span>
              <span className="text-sm font-medium max-w-[140px] truncate">{user?.name || user?.email}</span>
            </div>
            <Button variant="ghost" size="icon" data-testid="settings-btn" className="rounded-full" onClick={() => setSettingsOpen(true)}>
              <Settings className="w-5 h-5" />
            </Button>
            <Button variant="ghost" size="icon" data-testid="theme-toggle" className="rounded-full" onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>
              {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Button variant="ghost" size="icon" data-testid="logout-btn" className="rounded-full" onClick={logout}>
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
        {tabs && <div className="max-w-7xl mx-auto px-4 sm:px-6">{tabs}</div>}
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">{children}</main>
      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
    </div>
  );
};

export const StatusBadge = ({ status }) => {
  const map = {
    active: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
    completed: "bg-stone-500/15 text-stone-500 border-stone-500/30",
    pending: "bg-amber-500/15 text-amber-600 border-amber-500/30",
    approved: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
    denied: "bg-red-500/15 text-red-600 border-red-500/30",
  };
  return (
    <span data-testid="status-badge" className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border capitalize ${map[status] || "bg-secondary text-muted-foreground border-border"}`}>
      {status}
    </span>
  );
};
